<script setup lang="ts">
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { BadgeSeverity, ButtonSeverity, TuBadge, TuButton } from "@tinyurldev/tu-ui-vue";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
interface Props {
  isChangingDevice?: boolean;
  isDisabling?: boolean;
}

withDefaults(defineProps<Props>(), {
  isChangingDevice: false,
  isDisabling: false,
});

//
// Emit
interface Events {
  (e: "change-device"): void;
  (e: "disable"): void;
}

const emit = defineEmits<Events>();
</script>

<template>
  <div
    class="two-factor-manage"
    :class="{ 'two-factor-manage--mobile': isMobileView }"
  >
    <TuBadge
      class="two-factor-manage__badge _enabled_badge"
      :label="__('security_settings.two_factor_manage.enabled_badge')"
      :severity="BadgeSeverity.PRIMARY"
      icon-class="fa-solid fa-circle-check"
    />

    <p class="two-factor-manage__description">
      {{ __("security_settings.two_factor_manage.description") }}
    </p>

    <div class="two-factor-manage__actions">
      <TuButton
        class="_change_device"
        :label="__('security_settings.two_factor_manage.change_device')"
        is-outlined
        :severity="ButtonSeverity.PRIMARY"
        :is-disabled="isChangingDevice"
        :is-block="isMobileView"
        @click="emit('change-device')"
      />
      <TuButton
        class="_disable"
        :label="__('security_settings.two_factor_manage.disable')"
        is-outlined
        :severity="ButtonSeverity.ALERT"
        :is-disabled="isDisabling"
        :is-block="isMobileView"
        @click="emit('disable')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.two-factor-manage {
  $self: &;

  display: flex;
  flex-flow: column;
  align-items: flex-start;
  gap: spacing.$xxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__badge {
    text-align: start;
  }

  &__description {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__actions {
    display: flex;
    flex-flow: wrap;
    gap: spacing.$xxs;
  }

  &--mobile {
    #{ $self }__actions {
      flex-flow: column;
      align-self: stretch;
    }
  }
}
</style>
